import { http } from '@/services/http'
import type { PaginatedResponse } from '@/types/pagination'
import type {
  Invoice,
  DelinquencyStats,
  DelinquentStudent,
  RevenueReport,
  PlanRevenueReport,
  FinancialSummary,
  StudentFinancial,
  BillingConfig,
} from '@/types/financial'
import type {
  InvoiceApiResponse,
  ListInvoicesParams,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  ManualPaymentRequest,
  InvoiceStatsResponse,
  DelinquencyStatsApiResponse,
  DelinquentStudentApiResponse,
  ListDelinquentStudentsParams,
  RevenueReportApiResponse,
  PlanRevenueReportApiResponse,
  FinancialSummaryApiResponse,
  StudentFinancialApiResponse,
  BillingConfigApiResponse,
  UpdateBillingConfigRequest,
  TestConnectionRequest,
  TestConnectionResponse,
  CashflowReportApiResponse,
  AgingReportApiResponse,
  PaymentMethodsReportApiResponse,
  PlanHistoryReportApiResponse,
  MrrReportApiResponse,
  ForecastReportApiResponse,
} from './financial.dto'
import {
  mapInvoiceApiResponseToInvoice,
  mapDelinquencyStatsApiResponseToDelinquencyStats,
  mapDelinquentStudentApiResponseToDelinquentStudent,
  mapRevenueReportApiResponseToRevenueReport,
  mapPlanRevenueReportApiResponseToPlanRevenueReport,
  mapFinancialSummaryApiResponseToFinancialSummary,
  mapStudentFinancialApiResponseToStudentFinancial,
  mapBillingConfigApiResponseToBillingConfig,
} from './financial.mapper'

function buildInvoicesUrl(params?: ListInvoicesParams): string {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.set('limit', params.limit.toString())
  if (params?.cursor) queryParams.set('cursor', params.cursor)
  if (params?.search) queryParams.set('search', params.search)
  if (params?.status) queryParams.set('status', params.status)
  if (params?.studentId) queryParams.set('studentId', params.studentId)
  if (params?.planId) queryParams.set('planId', params.planId)
  if (params?.type) queryParams.set('type', params.type)
  if (params?.startDate) queryParams.set('startDate', params.startDate)
  if (params?.endDate) queryParams.set('endDate', params.endDate)
  if (params?.sortBy) queryParams.set('sortBy', params.sortBy)
  if (params?.sortDir) queryParams.set('sortDir', params.sortDir)

  const queryString = queryParams.toString()

  return queryString ? `/financial/invoices?${queryString}` : '/financial/invoices'
}

function buildDelinquentStudentsUrl(params?: ListDelinquentStudentsParams): string {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.set('limit', params.limit.toString())
  if (params?.cursor) queryParams.set('cursor', params.cursor)
  if (params?.search) queryParams.set('search', params.search)
  if (params?.sortBy) queryParams.set('sortBy', params.sortBy)
  if (params?.sortDir) queryParams.set('sortDir', params.sortDir)

  const queryString = queryParams.toString()

  return queryString
    ? `/financial/delinquency/students?${queryString}`
    : '/financial/delinquency/students'
}

export const financialService = {
  // Invoices
  listInvoices: async (params?: ListInvoicesParams): Promise<PaginatedResponse<Invoice>> => {
    const url = buildInvoicesUrl(params)
    const response = await http.get<PaginatedResponse<InvoiceApiResponse>>(url)

    return {
      data: response.data.map(mapInvoiceApiResponseToInvoice),
      meta: response.meta,
    }
  },

  getInvoiceById: async (id: string): Promise<Invoice> => {
    const response = await http.get<InvoiceApiResponse>(`/financial/invoices/${id}`)

    return mapInvoiceApiResponseToInvoice(response)
  },

  createInvoice: async (data: CreateInvoiceRequest): Promise<Invoice> => {
    const response = await http.post<InvoiceApiResponse>('/financial/invoices', data)

    return mapInvoiceApiResponseToInvoice(response)
  },

  updateInvoice: async (id: string, data: UpdateInvoiceRequest): Promise<Invoice> => {
    const response = await http.patch<InvoiceApiResponse>(`/financial/invoices/${id}`, data)

    return mapInvoiceApiResponseToInvoice(response)
  },

  cancelInvoice: async (id: string): Promise<Invoice> => {
    const response = await http.delete<InvoiceApiResponse>(`/financial/invoices/${id}`)

    return mapInvoiceApiResponseToInvoice(response)
  },

  getInvoiceStats: async (): Promise<InvoiceStatsResponse> => {
    return http.get<InvoiceStatsResponse>('/financial/invoices/stats')
  },

  // Payments
  registerManualPayment: async (data: ManualPaymentRequest): Promise<Invoice> => {
    const response = await http.post<InvoiceApiResponse>('/financial/payments/manual', data)

    return mapInvoiceApiResponseToInvoice(response)
  },

  // Delinquency
  getDelinquencyStats: async (): Promise<DelinquencyStats> => {
    const response = await http.get<DelinquencyStatsApiResponse>('/financial/delinquency')

    return mapDelinquencyStatsApiResponseToDelinquencyStats(response)
  },

  listDelinquentStudents: async (
    params?: ListDelinquentStudentsParams,
  ): Promise<PaginatedResponse<DelinquentStudent>> => {
    const url = buildDelinquentStudentsUrl(params)
    const response = await http.get<PaginatedResponse<DelinquentStudentApiResponse>>(url)

    return {
      data: response.data.map(mapDelinquentStudentApiResponseToDelinquentStudent),
      meta: response.meta,
    }
  },

  // Reports
  getRevenueReport: async (): Promise<RevenueReport> => {
    const response = await http.get<RevenueReportApiResponse>('/financial/reports/revenue')

    return mapRevenueReportApiResponseToRevenueReport(response)
  },

  getPlanRevenueReport: async (): Promise<PlanRevenueReport[]> => {
    const response = await http.get<PlanRevenueReportApiResponse>('/financial/reports/by-plan')

    return mapPlanRevenueReportApiResponseToPlanRevenueReport(response)
  },

  getFinancialSummary: async (): Promise<FinancialSummary> => {
    const response = await http.get<FinancialSummaryApiResponse>('/financial/reports/summary')

    return mapFinancialSummaryApiResponseToFinancialSummary(response)
  },

  // Student financial
  getStudentFinancial: async (studentId: string): Promise<StudentFinancial> => {
    const response = await http.get<StudentFinancialApiResponse>(
      `/financial/students/${studentId}/financial`,
    )

    return mapStudentFinancialApiResponseToStudentFinancial(response)
  },

  // Billing config
  getBillingConfig: async (): Promise<BillingConfig | null> => {
    const response = await http.get<BillingConfigApiResponse & { configured?: boolean }>(
      '/companies/current/billing-config',
    )

    if ('configured' in response && response.configured === false) return null

    return mapBillingConfigApiResponseToBillingConfig(response as BillingConfigApiResponse)
  },

  updateBillingConfig: async (data: UpdateBillingConfigRequest): Promise<BillingConfig> => {
    const response = await http.patch<BillingConfigApiResponse>(
      '/companies/current/billing-config',
      data,
    )

    return mapBillingConfigApiResponseToBillingConfig(response)
  },

  testAsaasConnection: async (data: TestConnectionRequest): Promise<TestConnectionResponse> => {
    return http.post<TestConnectionResponse>('/companies/current/billing-config/test', data)
  },

  getMunicipalServices: async (): Promise<Array<{ id: string; description: string }>> => {
    return http.get<Array<{ id: string; description: string }>>(
      '/companies/current/billing-config/services',
    )
  },

  // NFS-e
  issueNfse: async (invoiceId: string): Promise<Invoice> => {
    const response = await http.post<InvoiceApiResponse>(`/financial/invoices/${invoiceId}/nfse`)

    return mapInvoiceApiResponseToInvoice(response)
  },

  // Reports (detailed)
  getCashflowReport: async (startDate: string, endDate: string): Promise<CashflowReportApiResponse> => {
    return http.get<CashflowReportApiResponse>(
      `/financial/reports/cash-flow?startDate=${startDate}&endDate=${endDate}`,
    )
  },

  getAgingReport: async (): Promise<AgingReportApiResponse> => {
    return http.get<AgingReportApiResponse>('/financial/reports/aging')
  },

  getPaymentMethodsReport: async (startDate: string, endDate: string): Promise<PaymentMethodsReportApiResponse> => {
    return http.get<PaymentMethodsReportApiResponse>(
      `/financial/reports/payment-methods?startDate=${startDate}&endDate=${endDate}`,
    )
  },

  getPlanHistoryReport: async (startDate: string, endDate: string): Promise<PlanHistoryReportApiResponse> => {
    return http.get<PlanHistoryReportApiResponse>(
      `/financial/reports/plan-history?startDate=${startDate}&endDate=${endDate}`,
    )
  },

  getMRRReport: async (): Promise<MrrReportApiResponse> => {
    return http.get<MrrReportApiResponse>('/financial/reports/mrr')
  },

  getForecastReport: async (days: number): Promise<ForecastReportApiResponse> => {
    return http.get<ForecastReportApiResponse>(`/financial/reports/projection?days=${days}`)
  },
}
